import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Download, CheckCircle2, AlertTriangle, Sparkles, ArrowRight, ArrowLeft, Trash2, ShieldCheck, Loader2 } from "lucide-react";

const NARRATIVE_KEYS = ["exec_brief", "basis", "materiality", "governance", "responsible", "resilience", "outlook"];
const NARRATIVE_LABELS = {
  exec_brief: "Executive brief", basis: "Reporting basis statement", materiality: "Materiality narrative",
  governance: "Governance narrative", responsible: "Responsible-business narrative",
  resilience: "Resilience narrative", outlook: "Future outlook",
};
const STEPS = ["Scope", "Sections", "Data & gaps", "Narrative", "Generate"];

function AlignStrip({ a }) {
  if (!a || (!a.intl?.length && !a.local?.length)) return null;
  return (
    <div className="text-[10px] text-emerald-600 mt-1" data-testid="align-strip">
      Aligned to — {a.intl?.length ? `Intl: ${a.intl.join(", ")}` : ""}{a.intl?.length && a.local?.length ? " · " : ""}{a.local?.length ? `SG: ${a.local.join(", ")}` : ""}
    </div>
  );
}

async function downloadPdf(rid, name) {
  try {
    const r = await api.get(`/sme-report/${rid}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `${(name || "sustainability-report").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
    a.click();
    toast.success("Report PDF downloaded");
  } catch { toast.error("Could not download PDF"); }
}

export default function SustainabilityReport() {
  const [view, setView] = useState("library");
  const [reports, setReports] = useState(null);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);

  // wizard state
  const [period, setPeriod] = useState("");
  const [audiences, setAudiences] = useState([]);
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [narrative, setNarrative] = useState({});
  const [created, setCreated] = useState(null);
  const [compare, setCompare] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [uploading, setUploading] = useState(null);
  const [gapTasks, setGapTasks] = useState([]);

  const loadReports = () => api.get("/sme-report").then((r) => setReports(r.data.reports)).catch(() => setReports([]));
  useEffect(() => { loadReports(); }, []);

  const startWizard = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sme-report/config");
      setConfig(data);
      setPeriod(data.periods[0] || "");
      setAudiences(["Customers & buyers"]);
      setSelected([...new Set([...data.default_selection, "scope3"])]);
      setNarrative({}); setPreview(null); setCreated(null); setStep(0);
      setCompare(false); setLogoUrl(""); setCoverUrl(""); setGapTasks([]);
      setView("wizard");
    } catch { toast.error("Could not load report configuration"); }
    finally { setBusy(false); }
  };

  const toggleSection = (key, locked) => {
    if (locked) return;
    setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  };
  const toggleAudience = (a) => setAudiences((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sme-report/preview", { period, sections: selected, compare });
      setPreview(data); setStep(2);
    } catch { toast.error("Could not build data preview"); }
    finally { setBusy(false); }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("file", file);
      const { data } = await api.post("/sme-report/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      (kind === "logo" ? setLogoUrl : setCoverUrl)(data.url);
      toast.success(`${kind} uploaded`);
    } catch { toast.error("Upload failed — use PNG/JPEG/WebP under 5MB"); }
    finally { setUploading(null); }
  };

  const aiDraft = async (section) => {
    setAiBusy(section);
    try {
      const { data } = await api.post("/sme-report/ai-draft", { section, period });
      setNarrative((n) => ({ ...n, [section]: data.draft }));
      toast.success("AI draft inserted — review required");
    } catch { toast.error("AI draft unavailable"); }
    finally { setAiBusy(null); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sme-report", { period, audiences, sections: selected, narrative, compare, logo_url: logoUrl || null, cover_url: coverUrl || null });
      setCreated(data); setStep(4);
      loadReports();
      api.get(`/sme-report/${data.id}/gap-tasks`).then((r) => setGapTasks(r.data.tasks)).catch(() => {});
      toast.success("Report generated (draft)");
    } catch { toast.error("Could not generate report"); }
    finally { setBusy(false); }
  };

  const saveToLibrary = async (rid) => {
    try {
      await api.post(`/sme-report/${rid}/save-to-library`);
      toast.success("Saved to library & Document Inbox");
      loadReports();
    } catch { toast.error("Could not save to library"); }
  };

  const assignGap = async (label, section) => {
    const assignee = window.prompt(`Assign "${label}" to (email or name):`, "");
    if (assignee === null) return;
    try {
      await api.post(`/sme-report/${created.id}/gap-task`, { label, section, assignee: assignee || "Unassigned" });
      const r = await api.get(`/sme-report/${created.id}/gap-tasks`);
      setGapTasks(r.data.tasks);
      toast.success("Gap task created");
    } catch { toast.error("Could not create task"); }
  };

  const toggleGapTask = async (gid, status) => {
    await api.patch(`/sme-report/gap-task/${gid}`, { status: status === "done" ? "open" : "done" });
    const r = await api.get(`/sme-report/${created.id}/gap-tasks`);
    setGapTasks(r.data.tasks);
  };

  const reportGaps = () => {
    if (!created?.data) return [];
    const gaps = [];
    Object.entries(created.data).forEach(([sk, sec]) => {
      if (!sec || typeof sec !== "object") return;
      Object.values(sec).forEach((v) => { if (Array.isArray(v)) v.forEach((it) => { if (it && typeof it === "object" && it.present === false) gaps.push({ label: it.label, section: sk }); }); });
    });
    return gaps;
  };

  const approve = async (rid) => {
    try {
      await api.post(`/sme-report/${rid}/approve`);
      toast.success("Report approved for publication");
      loadReports();
      if (created?.id === rid) setCreated((c) => ({ ...c, status: "approved" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval blocked");
    }
  };

  const del = async (rid) => {
    if (!window.confirm("Delete this report?")) return;
    await api.delete(`/sme-report/${rid}`);
    loadReports();
  };

  // ---------------- Library view ----------------
  if (view === "library") {
    return (
      <div className="space-y-6" data-testid="sme-report-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold flex items-center gap-2"><FileText className="h-7 w-7 text-accent" /> Sustainability Report</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Generate a full Singapore SME Sustainability &amp; Business Resilience Report from data already in the platform. Evidence-led — figures are pulled from your records and gaps are shown honestly.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/report-studio"><Button variant="outline" data-testid="sme-open-studio"><Sparkles className="h-4 w-4" /> Report Studio</Button></Link>
            <Button variant="primary" onClick={startWizard} disabled={busy} data-testid="sme-new-report-btn"><Plus className="h-4 w-4" /> New report</Button>
          </div>
        </div>

        <div className="card-flat p-5">
          <div className="label-eyebrow mb-3">Report library</div>
          {reports === null ? <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
            : reports.length === 0 ? (
              <div className="py-10 text-center">
                <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">No reports yet. Create your first Sustainability &amp; Business Resilience Report.</p>
                <Button variant="outline" className="mt-4" onClick={startWizard} data-testid="sme-empty-new-btn"><Plus className="h-4 w-4" /> New report</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 border border-border rounded-md p-3 flex-wrap" data-testid={`sme-report-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">{r.entity} · {r.period}
                        <Badge variant={r.status === "approved" ? "success" : "warning"} className="text-[9px] uppercase">{r.status}</Badge>
                        {r.pdf_path && <Badge variant="outline" className="text-[9px]" data-testid={`sme-saved-${r.id}`}>saved</Badge>}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{r.section_count} sections · evidence coverage {r.coverage_pct}%</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.status !== "approved" && <Button size="sm" variant="outline" onClick={() => approve(r.id)} data-testid={`sme-approve-${r.id}`}><ShieldCheck className="h-3.5 w-3.5" /> Approve</Button>}
                      <Button size="sm" variant="outline" onClick={() => saveToLibrary(r.id)} data-testid={`sme-save-${r.id}`}><FileText className="h-3.5 w-3.5" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, `${r.entity}-${r.period}`)} data-testid={`sme-download-${r.id}`}><Download className="h-3.5 w-3.5" /> PDF</Button>
                      <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`sme-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    );
  }

  // ---------------- Wizard ----------------
  const canLock = (flag) => flag === "always";
  const grouped = (g) => (config?.sections || []).filter((s) => s.group === g);

  return (
    <div className="space-y-6" data-testid="sme-wizard">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><FileText className="h-6 w-6 text-accent" /> New Sustainability Report</h1>
        <Button variant="ghost" onClick={() => setView("library")} data-testid="sme-cancel">Back to library</Button>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="sme-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className={`text-xs px-3 py-1.5 rounded-full border ${i === step ? "bg-accent/10 border-accent text-accent font-medium" : i < step ? "border-emerald-500/40 text-emerald-600" : "border-border text-muted-foreground"}`}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      {/* Step 0: Scope */}
      {step === 0 && (
        <div className="card-flat p-5 space-y-4" data-testid="sme-step-scope">
          <div className="label-eyebrow">Scope</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><div className="text-xs text-muted-foreground mb-1">Reporting entity</div><div className="font-medium">{config?.entity}</div></div>
            <div><div className="text-xs text-muted-foreground mb-1">Industry module</div><div className="font-medium">{config?.industry || "—"}</div></div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Reporting period (FY)</div>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-full" data-testid="sme-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(config?.periods || []).map((p) => <SelectItem key={p} value={p} data-testid={`sme-period-${p}`}>FY{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Intended audience(s)</div>
            <div className="flex flex-wrap gap-2">
              {(config?.audiences_options || []).map((a) => (
                <button key={a} onClick={() => toggleAudience(a)} data-testid={`sme-aud-${a}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${audiences.includes(a) ? "bg-accent/10 border-accent text-accent" : "border-border text-muted-foreground"}`}>{a}</button>
              ))}
            </div>
          </div>

          {/* Period comparison */}
          <div className="flex items-start gap-2" data-testid="sme-compare-row">
            <button onClick={() => setCompare((v) => !v)} data-testid="sme-compare-toggle"
              className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center text-[10px] ${compare ? "bg-accent border-accent text-white" : "border-muted-foreground"}`}>{compare ? "✓" : ""}</button>
            <div><div className="text-sm font-medium">Show last-FY-vs-this-FY comparison</div>
              <div className="text-[11px] text-muted-foreground">Adds a prior-year column and change % per metric where prior data exists.</div></div>
          </div>

          {/* Branding */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sme-branding">
            {[{ k: "logo", val: logoUrl, set: setLogoUrl, label: "Company logo (optional)" },
              { k: "cover", val: coverUrl, set: setCoverUrl, label: "Cover image (optional)" }].map((f) => (
              <div key={f.k}>
                <div className="text-xs text-muted-foreground mb-1">{f.label}</div>
                <div className="flex items-center gap-2">
                  {f.val ? <img src={f.val.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${f.val}` : f.val} alt={f.k} className="h-10 w-16 object-contain border border-border rounded bg-white" data-testid={`sme-${f.k}-preview`} /> : null}
                  <label className="text-xs px-3 py-1.5 rounded-md border border-border cursor-pointer hover:bg-accent/5" data-testid={`sme-${f.k}-upload`}>
                    {uploading === f.k ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadImage(f.k, e.target.files[0])} />
                  </label>
                  {f.val && <button className="text-[11px] text-muted-foreground underline" onClick={() => f.set("")}>clear</button>}
                </div>
                <input value={f.val && f.val.startsWith("/api") ? "" : f.val} onChange={(e) => f.set(e.target.value)} placeholder="…or paste an image URL"
                  className="w-full text-xs rounded-md border border-border bg-background p-1.5 mt-1.5" data-testid={`sme-${f.k}-url`} />
              </div>
            ))}
          </div>

          <div className="flex justify-end"><Button variant="primary" onClick={() => setStep(1)} disabled={!period} data-testid="sme-scope-next">Next <ArrowRight className="h-4 w-4" /></Button></div>
        </div>
      )}

      {/* Step 1: Sections */}
      {step === 1 && (
        <div className="card-flat p-5 space-y-4" data-testid="sme-step-sections">
          <div className="label-eyebrow">Sections — each shows the standards it aligns to</div>
          {["front", "core", "back"].map((g) => (
            <div key={g}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2 mb-1">{g === "front" ? "Front matter" : g === "core" ? "Core narrative + data" : "Back matter"}</div>
              <div className="space-y-1.5">
                {grouped(g).map((s) => {
                  const locked = canLock(s.flag);
                  const on = selected.includes(s.key) || locked;
                  return (
                    <div key={s.key} className="border border-border rounded-md p-2.5" data-testid={`sme-section-${s.key}`}>
                      <div className="flex items-start gap-2">
                        <button onClick={() => toggleSection(s.key, locked)} disabled={locked} data-testid={`sme-toggle-${s.key}`}
                          className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center text-[10px] ${on ? "bg-accent border-accent text-white" : "border-muted-foreground"}`}>{on ? "✓" : ""}</button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">{s.title}
                            {locked && <Badge variant="outline" className="text-[8px]">always</Badge>}
                            {s.flag === "optional" && <Badge variant="outline" className="text-[8px]">optional</Badge>}
                          </div>
                          <AlignStrip a={s.alignment} />
                          {s.subtoggles && on && s.subtoggles.map((t) => (
                            <label key={t.key} className="flex items-center gap-2 mt-1.5 ml-1 text-xs text-muted-foreground cursor-pointer" data-testid={`sme-subtoggle-${t.key}`}>
                              <button onClick={() => toggleSection(t.key, false)} className={`h-3.5 w-3.5 rounded border flex items-center justify-center text-[9px] ${selected.includes(t.key) ? "bg-accent border-accent text-white" : "border-muted-foreground"}`}>{selected.includes(t.key) ? "✓" : ""}</button>
                              Include {t.title}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4" /> Back</Button><Button variant="primary" onClick={runPreview} disabled={busy} data-testid="sme-sections-next">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Review data <ArrowRight className="h-4 w-4" /></>}</Button></div>
        </div>
      )}

      {/* Step 2: Data & gaps */}
      {step === 2 && preview && (
        <div className="card-flat p-5 space-y-4" data-testid="sme-step-data">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="label-eyebrow">Data &amp; gaps review</div>
            <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">Evidence coverage</span><span className="font-mono font-bold" data-testid="sme-coverage-pct">{preview.coverage.coverage_pct}%</span></div>
          </div>
          <Progress value={preview.coverage.coverage_pct} className="h-2" />
          <p className="text-[11px] text-muted-foreground">{preview.coverage.present} of {preview.coverage.total} tracked data points present. Gaps are shown as "to complete" and are never estimated. Approval requires ≥80% coverage.</p>
          <div className="space-y-3 max-h-[420px] overflow-auto pr-1">
            {Object.entries(preview.data).map(([sk, sec]) => {
              const items = [];
              Object.values(sec).forEach((v) => { if (Array.isArray(v)) v.forEach((it) => { if (it && typeof it === "object" && "present" in it) items.push(it); }); });
              if (!items.length) return null;
              return (
                <div key={sk} className="border border-border rounded-md p-3" data-testid={`sme-data-${sk}`}>
                  <div className="text-xs font-medium capitalize mb-1.5">{sk.replace(/_/g, " ")}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {items.map((it, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        {it.present ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                        <span className={it.present ? "" : "text-amber-600"}>{it.label}{it.present ? `: ${it.value ?? ""} ${it.unit || ""}` : " — gap"}{it.present && it.prior != null ? ` (prior ${it.prior}${it.delta_pct != null ? `, Δ ${it.delta_pct > 0 ? "+" : ""}${it.delta_pct}%` : ""})` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4" /> Back</Button><Button variant="primary" onClick={() => setStep(3)} data-testid="sme-data-next">Add narrative <ArrowRight className="h-4 w-4" /></Button></div>
        </div>
      )}

      {/* Step 3: Narrative */}
      {step === 3 && (
        <div className="card-flat p-5 space-y-4" data-testid="sme-step-narrative">
          <div className="label-eyebrow">Narrative — optional AI drafts are labelled "review required"</div>
          {NARRATIVE_KEYS.filter((k) => selected.includes(k)).map((k) => (
            <div key={k} data-testid={`sme-narrative-${k}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium">{NARRATIVE_LABELS[k]}</div>
                <Button size="sm" variant="outline" onClick={() => aiDraft(k)} disabled={aiBusy === k} data-testid={`sme-ai-${k}`}>
                  {aiBusy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI draft
                </Button>
              </div>
              <textarea value={narrative[k] || ""} onChange={(e) => setNarrative((n) => ({ ...n, [k]: e.target.value }))}
                rows={3} placeholder="Enter narrative text…" data-testid={`sme-narrative-input-${k}`}
                className="w-full text-sm rounded-md border border-border bg-background p-2.5" />
            </div>
          ))}
          {!NARRATIVE_KEYS.some((k) => selected.includes(k)) && <p className="text-sm text-muted-foreground">No narrative sections selected.</p>}
          <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4" /> Back</Button><Button variant="primary" onClick={generate} disabled={busy} data-testid="sme-generate-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Generate report <ArrowRight className="h-4 w-4" /></>}</Button></div>
        </div>
      )}

      {/* Step 4: Generated */}
      {step === 4 && created && (
        <div className="card-flat p-6 space-y-4 text-center" data-testid="sme-step-done">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
          <h3 className="font-display text-xl font-bold">Report generated</h3>
          <div className="flex items-center justify-center gap-2">
            <Badge variant={created.status === "approved" ? "success" : "warning"} className="uppercase text-[9px]">{created.status}</Badge>
            <span className="text-sm text-muted-foreground">Evidence coverage {created.coverage?.coverage_pct}%</span>
          </div>
          {created.status !== "approved" && <p className="text-xs text-amber-600 max-w-md mx-auto">Draft — not approved for publication. Approval requires ≥80% evidence coverage (hard gate).</p>}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button variant="primary" onClick={() => downloadPdf(created.id, `${created.entity}-${created.period}`)} data-testid="sme-done-download"><Download className="h-4 w-4" /> Download PDF</Button>
            <Button variant="outline" onClick={() => saveToLibrary(created.id)} data-testid="sme-done-save"><FileText className="h-4 w-4" /> Save to library</Button>
            {created.status !== "approved" && <Button variant="outline" onClick={() => approve(created.id)} data-testid="sme-done-approve"><ShieldCheck className="h-4 w-4" /> Approve for publication</Button>}
            <Button variant="ghost" onClick={() => setView("library")} data-testid="sme-done-library">Go to library</Button>
          </div>

          {/* Gap to-dos */}
          {(reportGaps().length > 0 || gapTasks.length > 0) && (
            <div className="text-left border-t border-border pt-4 mt-2" data-testid="sme-gap-panel">
              <div className="label-eyebrow mb-2">Data gaps — assign to complete before approval</div>
              {reportGaps().length > 0 && (
                <div className="space-y-1 mb-3">
                  {reportGaps().map((g, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`sme-gap-${i}`}>
                      <span className="text-amber-600 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {g.label} <span className="text-muted-foreground capitalize">· {g.section.replace(/_/g, " ")}</span></span>
                      <Button size="sm" variant="outline" onClick={() => assignGap(g.label, g.section)} data-testid={`sme-assign-${i}`}>Assign</Button>
                    </div>
                  ))}
                </div>
              )}
              {gapTasks.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Gap to-dos ({gapTasks.filter((t) => t.status === "open").length} open)</div>
                  {gapTasks.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-2 text-xs border border-border rounded px-2 py-1.5" data-testid={`sme-task-${t.id}`}>
                      <span className={t.status === "done" ? "line-through text-muted-foreground" : ""}>{t.label} → <span className="font-medium">{t.assignee}</span></span>
                      <button onClick={() => toggleGapTask(t.id, t.status)} className="text-[11px] underline text-accent" data-testid={`sme-task-toggle-${t.id}`}>{t.status === "done" ? "Reopen" : "Mark done"}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
